import React from 'react';
import { useParams } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { CmsLink as Link, EditableText } from '../content/EditableText';
import { useCmsContent } from '../content/CmsContentContext';

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const { content } = useCmsContent();
  const { services } = content;
  const service = services.find(s => s.id === serviceId);
  const serviceIndex = services.findIndex(s => s.id === serviceId);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-gray-900 mb-4"><EditableText contentKey="pages.serviceDetail.notFound" /></h1>
          <Link to="/" className="text-brand-700 hover:text-brand-800">
            <EditableText contentKey="pages.serviceDetail.returnHome" multiline={false} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-96 bg-gray-900">
        <img
          src={service.image}
          alt={service.title}
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-900/70 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-white max-w-2xl">
            <h1 className="text-4xl md:text-5xl font-light mb-4"><EditableText contentKey={`services.${serviceIndex}.title`} /></h1>
            <p className="text-xl"><EditableText contentKey={`services.${serviceIndex}.description`} /></p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-light text-gray-900 mb-6"><EditableText contentKey="pages.serviceDetail.about" /></h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-8">
              <EditableText contentKey={`services.${serviceIndex}.fullDescription`} />
            </p>

            <h3 className="text-2xl font-light text-gray-900 mb-4"><EditableText contentKey="pages.serviceDetail.benefits" /></h3>
            <ul className="space-y-3 mb-8">
              {service.benefits.map((benefit, index) => (
                <li key={index} className="flex items-start">
                  <Check className="h-6 w-6 text-brand-700 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 text-lg"><EditableText contentKey={`services.${serviceIndex}.benefits.${index}`} /></span>
                </li>
              ))}
            </ul>

            <div className="bg-brand-50 p-8 rounded-lg mt-12">
              <h3 className="text-2xl font-light text-gray-900 mb-4"><EditableText contentKey="pages.serviceDetail.readyTitle" /></h3>
              <p className="text-gray-700 mb-6">
                <EditableText contentKey="pages.serviceDetail.readyBody" />
              </p>
              <Link
                to="/book-appointment"
                className="inline-flex items-center px-8 py-3 bg-brand-700 text-white rounded-md hover:bg-brand-800 transition-colors text-lg font-medium"
              >
                <EditableText contentKey="pages.serviceDetail.book" multiline={false} /> <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-12">
            <EditableText contentKey="pages.serviceDetail.otherServices" />
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services
              .filter(s => s.id !== serviceId)
              .map((otherService) => {
                const otherIndex = services.findIndex((candidate) => candidate.id === otherService.id);
                return (
                <Link
                  key={otherService.id}
                  to={`/services/${otherService.id}`}
                  className="group bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden"
                >
                  <div className="flex">
                    <img
                      src={otherService.image}
                      alt={otherService.title}
                      className="w-1/3 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="p-6 flex-1">
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        <EditableText contentKey={`services.${otherIndex}.title`} />
                      </h3>
                      <p className="text-gray-600 line-clamp-3"><EditableText contentKey={`services.${otherIndex}.description`} /></p>
                    </div>
                  </div>
                </Link>
                );
              })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
